/* A2P 10DLC Compliance Status card.
 *
 * Shows the user's brand + campaigns status pulled live from Twilio's
 * Messaging API. Color-codes each entry (PENDING/IN_PROGRESS/VERIFIED/FAILED)
 * so the user can tell at a glance whether SMS is unblocked.
 *
 * Auto-polls every 30s while ANY entry is still IN_PROGRESS / PENDING so
 * the user sees the moment a carrier flips to VERIFIED.
 *
 * Renders nothing until Twilio is connected (compliance is meaningless
 * without an account). Backend returns `connected: false` in that case.
 */
import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ExternalLink,
  Bell,
  Copy,
  Check,
} from "lucide-react";

const STATUS_COLOR = {
  VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  IN_REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  IN_PROGRESS: "bg-sky-100 text-sky-800 border-sky-200",
  FAILED: "bg-rose-100 text-rose-800 border-rose-200",
  SUSPENDED: "bg-rose-100 text-rose-800 border-rose-200",
};

const STATUS_ICON = {
  VERIFIED: CheckCircle2,
  APPROVED: CheckCircle2,
  PENDING: Clock,
  IN_REVIEW: Clock,
  IN_PROGRESS: Loader2,
  FAILED: AlertTriangle,
  SUSPENDED: AlertTriangle,
};

function StatusPill({ status }) {
  const up = (status || "UNKNOWN").toUpperCase();
  const cls = STATUS_COLOR[up] || "bg-stone-100 text-stone-700 border-stone-200";
  const Icon = STATUS_ICON[up] || Clock;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={`compliance-pill-${up.toLowerCase()}`}
    >
      <Icon size={10} className={up === "IN_PROGRESS" ? "animate-spin" : ""} />
      {up}
    </span>
  );
}

function isPolling(snapshot) {
  if (!snapshot) return false;
  const everyStatus = [
    ...(snapshot.brands || []).map((b) => (b.status || "").toUpperCase()),
    ...(snapshot.campaigns || []).map((c) => (c.status || "").toUpperCase()),
  ];
  return everyStatus.some((s) => s === "PENDING" || s === "IN_PROGRESS" || s === "IN_REVIEW");
}

export default function TwilioComplianceCard() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const prevReady = useRef(null);

  const fetchStatus = async (force = false) => {
    if (force) setRefreshing(true);
    try {
      const { data } = await api.get("/twilio/compliance-status", {
        params: force ? { force_refresh: true } : {},
      });
      setSnapshot(data);
      if (prevReady.current === false && data.ready_to_send === true) {
        toast.success("🎉 SMS unblocked — your A2P campaign is VERIFIED!");
      }
      prevReady.current = data.ready_to_send;
    } catch (e) {
      const code = e?.response?.status;
      if (code === 402 || code === 403) {
        setSnapshot({ hidden: true });
      } else if (force) {
        toast.error("Could not refresh compliance status.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadWebhook = async () => {
    try {
      const { data } = await api.get("/twilio/a2p-webhook-url");
      setWebhook(data);
      setShowNotify(true);
    } catch (e) {
      toast.error("Could not load webhook URL.");
    }
  };

  const copyWebhook = async () => {
    if (!webhook?.url) return;
    try {
      await navigator.clipboard.writeText(webhook.url);
      setCopied(true);
      toast.success("Webhook URL copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  useEffect(() => {
    fetchStatus(false);
  }, []);

  // Auto-poll every 30s while anything is pending. Stops once ready_to_send.
  useEffect(() => {
    if (!snapshot || snapshot.hidden) return;
    if (!isPolling(snapshot) && snapshot.ready_to_send) return;
    const id = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(id);
  }, [snapshot?.ready_to_send, JSON.stringify(snapshot?.campaigns?.map((c) => c.status))]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4">
        <Loader2 size={16} className="animate-spin inline mr-2" /> Checking A2P 10DLC compliance…
      </div>
    );
  }
  if (snapshot?.hidden) return null;
  if (!snapshot?.connected) {
    // Don't surface anything until Twilio is connected — the parent TwilioCard
    // is already telling the user to connect.
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4"
         data-testid="twilio-compliance-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
            snapshot.ready_to_send
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            {snapshot.ready_to_send
              ? <ShieldCheck size={18} className="text-emerald-600" />
              : <ShieldAlert size={18} className="text-amber-600" />}
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">A2P 10DLC Compliance</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5 max-w-md">
              Carrier-mandated approval for sending SMS via long-code numbers.
              {snapshot.ready_to_send
                ? " Your traffic will deliver at full reliability."
                : " Outbound SMS is paused until VERIFIED."}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchStatus(true)}
          disabled={refreshing}
          className="text-xs text-[var(--ink-2)] hover:text-emerald-600 inline-flex items-center gap-1 shrink-0"
          data-testid="compliance-refresh"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Top-level readiness banner */}
      {snapshot.ready_to_send ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-900 mb-4"
             data-testid="compliance-ready-banner">
          <b>Ready to send.</b> Your brand is approved and at least one campaign is VERIFIED.
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 mb-4"
             data-testid="compliance-blocked-banner">
          <b>SMS sending is paused.</b> {snapshot.reason}
        </div>
      )}

      {/* Brand list */}
      {snapshot.brands?.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-2">
            A2P Brand{snapshot.brands.length > 1 ? "s" : ""}
          </div>
          <div className="space-y-2">
            {snapshot.brands.map((b) => (
              <div key={b.sid}
                   className="border border-[var(--line)] rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                   data-testid={`compliance-brand-${b.sid}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{b.brand_type || "Standard"}</div>
                  <div className="font-mono text-[10px] text-[var(--ink-2)] truncate">{b.sid}</div>
                  {b.failure_reason && (
                    <div className="text-xs text-rose-700 mt-1 italic">{b.failure_reason}</div>
                  )}
                </div>
                <StatusPill status={b.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign list */}
      {snapshot.campaigns?.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-2">
            Campaigns
          </div>
          <div className="space-y-2">
            {snapshot.campaigns.map((c) => (
              <div key={c.service_sid}
                   className="border border-[var(--line)] rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                   data-testid={`compliance-campaign-${c.service_sid}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {c.service_name || c.use_case || "Campaign"}
                  </div>
                  <div className="text-[11px] text-[var(--ink-2)] truncate">
                    {c.use_case ? `Use case: ${c.use_case}` : c.campaign_id || c.service_sid}
                  </div>
                </div>
                <StatusPill status={c.status} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs text-[var(--ink-2)] italic mt-2">
          No campaigns registered yet. Register one in the Twilio Console under
          <a href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/campaigns"
             target="_blank" rel="noopener noreferrer"
             className="text-emerald-600 hover:underline ml-1 inline-flex items-center gap-0.5">
            Messaging → Regulatory Compliance <ExternalLink size={10} />
          </a>.
        </div>
      )}

      {/* Push-based status notifications (Twilio → us). One-time setup so
          we don't have to poll while the tab is closed. */}
      <div className="mt-4 pt-4 border-t border-[var(--line)]">
        {!showNotify ? (
          <button
            type="button"
            onClick={loadWebhook}
            className="text-xs inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-800 font-semibold"
            data-testid="compliance-enable-push"
          >
            <Bell size={12} /> Enable push notifications (1-time setup)
          </button>
        ) : webhook && (
          <div data-testid="compliance-webhook-panel">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-2 flex items-center gap-1.5">
              <Bell size={11} /> Push notification setup
            </div>
            <div className="text-xs text-[var(--ink-2)] mb-2">
              Paste this URL into Twilio Console so we get notified the instant a carrier
              flips your status — even when iSpeedToClose isn&apos;t open:
            </div>
            <div className="flex items-stretch gap-2 mb-3">
              <code className="flex-1 text-[11px] font-mono bg-[var(--surface-2)] border border-[var(--line)] rounded-lg px-2.5 py-2 truncate"
                    data-testid="compliance-webhook-url">
                {webhook.url}
              </code>
              <button
                type="button"
                onClick={copyWebhook}
                className="shrink-0 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="compliance-webhook-copy"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <ol className="text-xs text-[var(--ink-2)] list-decimal list-inside space-y-1">
              {webhook.instructions?.map((step, i) => (
                <li key={i}>{step.replace(/^\d+\.\s*/, "")}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {snapshot.cache_age_seconds !== undefined && (
        <div className="text-[10px] text-[var(--ink-2)] italic mt-3 text-right">
          Cached {snapshot.cache_age_seconds}s ago
        </div>
      )}
    </div>
  );
}
